import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Leaf, Archive, Sparkles, ArrowRight } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import ReviewsSection from '../components/ReviewsSection';
import { products } from '../data/products';

const Home = () => {
  const [stockStatus, setStockStatus] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stock-status`)
      .then(r => r.json())
      .then(data => setStockStatus(data))
      .catch(() => {});
  }, []);

  const features = [
    { icon: MapPin, title: '100% Australian Honey', description: 'Sourced from Victoria' },
    { icon: Leaf, title: 'Single-Origin', description: 'Pure, traceable, unblended' },
    { icon: Archive, title: 'Small Batch', description: 'Crafted with care, never mass-produced' },
    { icon: Sparkles, title: 'Raw Australian Honey', description: 'Natural quality, straight from the hive' }
  ];

  return (
    <div className="min-h-screen">
      <section className="relative h-screen flex items-center justify-center overflow-hidden pt-48" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_bee-harvest-6/artifacts/ty8yanp8_Boy%20Beekeeper%20Scene.jpg"
            alt="Beekeeper at work with hives"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/20 via-background/40 to-background"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl lg:text-7xl font-bold text-foreground mb-6 tracking-tighter leading-none"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="hero-headline"
          >
            Pure Australian Honey.<br />Straight from the hive.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-base md:text-lg text-foreground/80 mb-10 max-w-2xl mx-auto leading-relaxed"
            data-testid="hero-subheadline"
          >
            Raw, single-origin, small-batch honey from Victoria.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/products"
              className="px-8 py-4 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-all duration-300 font-semibold text-lg hover:scale-105"
              data-testid="shop-now-button"
            >
              Shop Honey
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-wide text-muted-foreground mb-4"></p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Three Unique Honeys
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {products.map((product, index) => (
              <ProductCard key={product.id} product={product} stockInfo={stockStatus[product.name]} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-muted/30" data-testid="why-rickys-bees-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Why Ricky's Bees?
            </h2>
            <p className="text-base md:text-lg text-foreground/70 max-w-2xl mx-auto">
              From hive to jar, nothing added, nothing taken away.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center p-8 rounded-2xl bg-background/50 border border-border hover:border-primary/30 transition-all duration-300"
                  data-testid={`feature-${index}`}
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
                    <Icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {feature.title}
                  </h3>
                  <p className="text-foreground/70 text-sm">
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <ReviewsSection />

      <section className="py-24 md:py-32" data-testid="inspiration-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Ways to Enjoy
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative overflow-hidden rounded-2xl md:rounded-[2rem] aspect-[4/3]"
            >
              <img
                src="https://static.prod-images.emergentagent.com/jobs/143c7995-c07b-4c81-a4f7-e0c409ad10c4/images/2f9fedebf266a15985296a4a7abf49789a9b471286933195ccf427738047063d.png"
                alt="Honey on Toast"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 to-transparent flex items-end">
                <div className="p-8 text-white">
                  <h3 className="text-2xl font-bold mb-2 text-white" style={{ fontFamily: 'Outfit, sans-serif', color: 'white' }}>Morning Ritual</h3>
                  <p className="text-sm opacity-90 text-white">Start your day with honey on toast</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="relative overflow-hidden rounded-2xl md:rounded-[2rem] aspect-[4/3]"
            >
              <img
                src="https://static.prod-images.emergentagent.com/jobs/143c7995-c07b-4c81-a4f7-e0c409ad10c4/images/1a4011abd2750d6555991f9d037d0dddbbbde7c25eb4eacc0f66a994a31ffa52.png"
                alt="Honey Texture"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 to-transparent flex items-end">
                <div className="p-8 text-white">
                  <h3 className="text-2xl font-bold mb-2 text-white" style={{ fontFamily: 'Outfit, sans-serif', color: 'white' }}>Natural Beauty</h3>
                  <p className="text-sm opacity-90 text-white">Use in skincare and wellness</p>
                </div>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center mt-12"
          >
            <Link
              to="/recipes"
              className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 font-semibold transition-colors"
              data-testid="view-recipes-link"
            >
              <span>Discover More Recipes</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
